import { action, computed, observable, makeObservable } from 'mobx';
import { CategoriaMinuta } from '@/modelo/Categoria';

export interface IModeloSelecionado {
    codigo: number;
    nome: string;
    conteudo: string;
    incluirAposPauta: boolean;
}

/**
 * Representa um processo parseado do CSV, com configuração opcional de servidor/tarefa.
 */
export interface IProcessoCSV {
    numeroProcesso: string;
    cpfServidor?: string;
    nomeTarefa?: string;
}

class ImportacaoLoteStore {
    // Estado do wizard
    abertoModal = false;
    abertoProgresso = false;
    stepIndex = 0;

    // Step 1 - CSV
    numerosProcessos: string[] = [];
    processosCSV: IProcessoCSV[] = [];
    csvComColunasExtras = false;
    nomeArquivo = '';

    // Step 2 - Servidor + Tarefa (usado apenas quando CSV NÃO tem colunas extras)
    servidorAtribuido = '-1';
    controleGabAtribuido = -1;

    // Step 3 - Criar minuta?
    criarMinuta = false;

    // Step 4 - Tipo de minuta
    tipoMinuta = 1;
    publicavel = false;
    incluirAposPauta = false;

    // Step 5 - Modelo
    modeloSelecionado: IModeloSelecionado = {
        codigo: -1,
        nome: '',
        conteudo: '',
        incluirAposPauta: false,
    };

    // Step 6 - Categorias
    categoriasSelecionadas: CategoriaMinuta[] = [];

    // Step 7 - Descrição signatário
    descricaoSignatario = '';

    // Progresso
    processando = false;
    faseAtual = '';
    progresso = 0;
    totalProcessos = 0;
    processados = 0;
    minutasGeradas = 0;
    mensagens: string[] = [];
    concluido = false;

    constructor() {
        makeObservable(this, {
            abertoModal: observable,
            abertoProgresso: observable,
            stepIndex: observable,
            numerosProcessos: observable,
            processosCSV: observable,
            csvComColunasExtras: observable,
            nomeArquivo: observable,
            servidorAtribuido: observable,
            controleGabAtribuido: observable,
            criarMinuta: observable,
            tipoMinuta: observable,
            publicavel: observable,
            incluirAposPauta: observable,
            modeloSelecionado: observable,
            categoriasSelecionadas: observable,
            descricaoSignatario: observable,
            processando: observable,
            faseAtual: observable,
            progresso: observable,
            totalProcessos: observable,
            processados: observable,
            minutasGeradas: observable,
            mensagens: observable,
            concluido: observable,
            podeAvancar: computed,
            totalSteps: computed,
            stepDistribuicaoVisivel: computed,
            abrirModal: action,
            fecharModal: action,
            abrirProgresso: action,
            fecharProgresso: action,
            avancarStep: action,
            voltarStep: action,
            resetar: action,
            resetarProgresso: action,
            setNumerosProcessos: action,
            setProcessosCSV: action,
        });
    }

    /**
     * Step de distribuição (servidor + tarefa) é visível apenas quando
     * o CSV NÃO traz as colunas de cpfServidor/nomeTarefa.
     */
    get stepDistribuicaoVisivel(): boolean {
        return !this.csvComColunasExtras;
    }

    get totalSteps(): number {
        // Steps base: CSV(0), [Servidor+Tarefa(1) - condicional], CriarMinuta?(1 ou 2)
        // Se criarMinuta: +4 (TipoMinuta, Modelo, Categorias, Signatário)
        const baseSteps = this.stepDistribuicaoVisivel ? 3 : 2;
        return this.criarMinuta ? baseSteps + 4 : baseSteps;
    }

    /**
     * Mapeia o stepIndex lógico para o significado real,
     * dependendo se o step de distribuição está visível ou não.
     */
    get stepSemantico(): string {
        if (this.stepDistribuicaoVisivel) {
            // 0=CSV, 1=Distribuição, 2=CriarMinuta, 3=Tipo, 4=Modelo, 5=Categorias, 6=Signatário
            const mapa = ['csv', 'distribuicao', 'criarMinuta', 'tipoMinuta', 'modelo', 'categorias', 'signatario'];
            return mapa[this.stepIndex] || '';
        } else {
            // 0=CSV, 1=CriarMinuta, 2=Tipo, 3=Modelo, 4=Categorias, 5=Signatário
            const mapa = ['csv', 'criarMinuta', 'tipoMinuta', 'modelo', 'categorias', 'signatario'];
            return mapa[this.stepIndex] || '';
        }
    }

    get podeAvancar(): boolean {
        switch (this.stepSemantico) {
            case 'csv':
                return this.numerosProcessos.length > 0;
            case 'distribuicao':
                return this.controleGabAtribuido > 0;
            case 'criarMinuta':
                return true;
            case 'tipoMinuta':
                return this.tipoMinuta > 0;
            case 'modelo':
                return this.modeloSelecionado.codigo >= 0;
            case 'categorias':
                return true;
            case 'signatario':
                return true;
            default:
                return false;
        }
    }

    abrirModal = () => {
        this.abertoModal = true;
    };

    fecharModal = () => {
        this.abertoModal = false;
        this.resetar();
    };

    abrirProgresso = () => {
        this.abertoProgresso = true;
    };

    fecharProgresso = () => {
        this.abertoProgresso = false;
        this.resetarProgresso();
    };

    avancarStep = () => {
        if (this.stepSemantico === 'criarMinuta' && !this.criarMinuta) {
            // Pula os steps de minuta, vai direto para execução
            return;
        }
        if (this.stepIndex < this.totalSteps - 1) {
            this.stepIndex++;
        }
    };

    voltarStep = () => {
        if (this.stepIndex > 0) {
            this.stepIndex--;
        }
    };

    resetar = () => {
        this.stepIndex = 0;
        this.numerosProcessos = [];
        this.processosCSV = [];
        this.csvComColunasExtras = false;
        this.nomeArquivo = '';
        this.servidorAtribuido = '-1';
        this.controleGabAtribuido = -1;
        this.criarMinuta = false;
        this.tipoMinuta = 1;
        this.publicavel = false;
        this.incluirAposPauta = false;
        this.modeloSelecionado = { codigo: -1, nome: '', conteudo: '', incluirAposPauta: false };
        this.categoriasSelecionadas = [];
        this.descricaoSignatario = '';
    };

    resetarProgresso = () => {
        this.processando = false;
        this.faseAtual = '';
        this.progresso = 0;
        this.totalProcessos = 0;
        this.processados = 0;
        this.minutasGeradas = 0;
        this.mensagens = [];
        this.concluido = false;
    };

    setNumerosProcessos = (numeros: string[], nomeArquivo: string) => {
        this.numerosProcessos = numeros;
        this.nomeArquivo = nomeArquivo;
    };

    setProcessosCSV = (processos: IProcessoCSV[], nomeArquivo: string, comColunasExtras: boolean) => {
        this.processosCSV = processos;
        this.numerosProcessos = processos.map(p => p.numeroProcesso);
        this.csvComColunasExtras = comColunasExtras;
        this.nomeArquivo = nomeArquivo;
    };
}

export default new ImportacaoLoteStore();
